/**
 * Offer Management Card Component
 * 판매자가 받은 제안들을 관리하는 카드
 */

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import {
  CheckCircle,
  DollarSign,
  Loader2,
  MessageCircle,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { DealContactPanel } from "./DealContactPanel";
import { ThreadView } from "./ThreadView";

interface Offer {
  id: number;
  product_id: number;
  buyer_id: string;
  seller_id: string;
  price_cents: number;
  counter_price_cents?: number;
  pickup_location?: string;
  pickup_time?: string;
  pickup_place_custom?: string;
  pickup_time_custom?: string;
  payment_method?: string;
  memo?: string;
  status:
    | "pending"
    | "accepted"
    | "countered"
    | "rejected"
    | "confirmed"
    | "completed"
    | "expired";
  confirm_pin?: string;
  dm_thread_id?: string;
  created_at: number;
  updated_at: number;
  buyer_name?: string;
  buyer_photo?: string;
}

interface OfferManagementCardProps {
  productId: number;
  sellerId: string;
}

export function OfferManagementCard({
  productId,
  sellerId,
}: OfferManagementCardProps) {
  const { toast } = useToast();
  const [counterDialogOpen, setCounterDialogOpen] = useState<number | null>(
    null
  );
  const [counterPrice, setCounterPrice] = useState<number | "">("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<number | null>(
    null
  );
  const [threadViewOpen, setThreadViewOpen] = useState<number | null>(null);

  // Fetch offers
  const { data: offers = [], isLoading } = useQuery<Offer[]>({
    queryKey: ["/api/market/offers", productId, sellerId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (productId) params.append("product_id", String(productId));
      if (sellerId) params.append("seller_id", sellerId);

      const res = await fetch(`/api/market/offers?${params.toString()}`);
      if (!res.ok) {
        if (res.status === 404) return [];
        throw new Error("Failed to fetch offers");
      }
      const json = await res.json();
      return Array.isArray(json.data)
        ? json.data
        : Array.isArray(json)
        ? json
        : [];
    },
    enabled: !!productId && !!sellerId,
  });

  // Accept offer
  const acceptOfferMutation = useMutation({
    mutationFn: async (offerId: number) => {
      return apiRequest(
        "POST",
        `/api/market/offers/${offerId}?action=accept`,
        {}
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/market/offers", productId, sellerId],
      });
      toast({
        title: "제안이 수락되었습니다",
        description: "구매자에게 알림이 전송되었습니다",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "수락 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  // Counter offer
  const counterOfferMutation = useMutation({
    mutationFn: async ({
      offerId,
      counterPriceCents,
    }: {
      offerId: number;
      counterPriceCents: number;
    }) => {
      return apiRequest(
        "POST",
        `/api/market/offers/${offerId}?action=counter`,
        { counter_price_cents: counterPriceCents }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/market/offers", productId, sellerId],
      });
      setCounterDialogOpen(null);
      setCounterPrice("");
      toast({
        title: "카운터 제안이 전송되었습니다",
        description: "구매자에게 알림이 전송되었습니다",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "카운터 제안 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  // Confirm offer
  const confirmOfferMutation = useMutation({
    mutationFn: async (offerId: number) => {
      return apiRequest(
        "POST",
        `/api/market/offers/${offerId}?action=confirm`,
        {}
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/market/offers", productId, sellerId],
      });
      toast({
        title: "거래 확정",
        description: "거래가 확정되었습니다. PIN이 생성되었습니다",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "확정 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  // Reject offer
  const rejectOfferMutation = useMutation({
    mutationFn: async (offerId: number) => {
      return apiRequest(
        "POST",
        `/api/market/offers/${offerId}?action=reject`,
        {}
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/market/offers", productId, sellerId],
      });
      toast({
        title: "제안이 거절되었습니다",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "거절 실패",
        description: error.message || "다시 시도해주세요",
        variant: "destructive",
      });
    },
  });

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(0)} AUD`;
  };

  const getStatusBadge = (status: Offer["status"]) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline">대기중</Badge>;
      case "accepted":
        return <Badge className="bg-green-600">수락됨</Badge>;
      case "countered":
        return <Badge className="bg-blue-600">카운터 제안</Badge>;
      case "rejected":
        return <Badge variant="destructive">거절됨</Badge>;
      case "confirmed":
        return <Badge className="bg-purple-600">거래 확정</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>받은 제안</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
            로딩 중...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (offers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>받은 제안</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            아직 받은 제안이 없습니다
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>받은 제안 ({offers.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {offers.map((offer) => (
          <div key={offer.id} className="border rounded-lg p-4 space-y-3">
            {/* Buyer Info */}
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src={offer.buyer_photo} />
                <AvatarFallback>
                  {offer.buyer_name?.charAt(0) || "?"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-medium">{offer.buyer_name || "구매자"}</p>
                <p className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(offer.created_at), {
                    addSuffix: true,
                    locale: ko,
                  })}
                </p>
              </div>
              {getStatusBadge(offer.status)}
            </div>

            <Separator />

            {/* Offer Details */}
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">제안 가격:</span>
                <span className="font-semibold">
                  {formatPrice(offer.price_cents)}
                </span>
              </div>
              {offer.counter_price_cents && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">카운터 가격:</span>
                  <span className="font-semibold text-blue-600">
                    {formatPrice(offer.counter_price_cents)}
                  </span>
                </div>
              )}
              {(offer.pickup_location || offer.pickup_place_custom) && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">픽업 장소:</span>
                  <span>
                    {offer.pickup_place_custom || offer.pickup_location}
                  </span>
                </div>
              )}
              {(offer.pickup_time || offer.pickup_time_custom) && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">픽업 시간:</span>
                  <span>{offer.pickup_time_custom || offer.pickup_time}</span>
                </div>
              )}
              {offer.payment_method && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">결제 방법:</span>
                  <span>{offer.payment_method}</span>
                </div>
              )}
              {offer.memo && (
                <div>
                  <span className="text-muted-foreground">메모:</span>
                  <p className="mt-1">{offer.memo}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            {offer.status === "pending" && (
              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  onClick={() => acceptOfferMutation.mutate(offer.id)}
                  disabled={acceptOfferMutation.isPending}
                  className="flex-1"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  수락
                </Button>
                <Dialog
                  open={counterDialogOpen === offer.id}
                  onOpenChange={(open) => {
                    setCounterDialogOpen(open ? offer.id : null);
                    if (open) {
                      setCounterPrice(offer.price_cents);
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="flex-1">
                      <DollarSign className="w-4 h-4 mr-1" />
                      카운터
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>카운터 제안</DialogTitle>
                      <DialogDescription>
                        다른 가격을 제안하세요
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="counter-price">제안 가격 (AUD)</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="counter-price"
                            type="number"
                            value={
                              counterPrice === ""
                                ? ""
                                : typeof counterPrice === "number"
                                ? counterPrice / 100
                                : ""
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                setCounterPrice("");
                              } else {
                                const cents = Math.round(
                                  parseFloat(value) * 100
                                );
                                if (!isNaN(cents) && cents >= 0) {
                                  setCounterPrice(cents);
                                }
                              }
                            }}
                            min="0"
                            step="0.01"
                          />
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            AUD
                          </span>
                        </div>
                      </div>
                      <Button
                        className="w-full"
                        onClick={() => {
                          if (
                            counterPrice !== "" &&
                            typeof counterPrice === "number"
                          ) {
                            counterOfferMutation.mutate({
                              offerId: offer.id,
                              counterPriceCents: counterPrice,
                            });
                          }
                        }}
                        disabled={
                          counterOfferMutation.isPending ||
                          !counterPrice ||
                          typeof counterPrice !== "number"
                        }
                      >
                        {counterOfferMutation.isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            전송 중...
                          </>
                        ) : (
                          "카운터 제안 전송"
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => rejectOfferMutation.mutate(offer.id)}
                  disabled={rejectOfferMutation.isPending}
                  className="flex-1"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  거절
                </Button>
              </div>
            )}

            {offer.status === "accepted" || offer.status === "countered" ? (
              <div className="pt-2 space-y-2">
                <Button
                  size="sm"
                  variant="default"
                  className="w-full"
                  onClick={() => {
                    confirmOfferMutation.mutate(offer.id);
                  }}
                  disabled={confirmOfferMutation.isPending}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  거래 확정하기
                </Button>
              </div>
            ) : null}

            {offer.status === "confirmed" && (
              <div className="pt-2 space-y-2">
                <Dialog
                  open={confirmDialogOpen === offer.id}
                  onOpenChange={(open) =>
                    setConfirmDialogOpen(open ? offer.id : null)
                  }
                >
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="w-full">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      거래 정보 보기
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>거래 확정 정보</DialogTitle>
                    </DialogHeader>
                    <DealContactPanel
                      offerId={offer.id}
                      confirmPin={offer.confirm_pin}
                      onComplete={() => {
                        setConfirmDialogOpen(null);
                        queryClient.invalidateQueries({
                          queryKey: ["/api/market/offers", productId, sellerId],
                        });
                      }}
                    />
                  </DialogContent>
                </Dialog>

                {offer.dm_thread_id && (
                  <Dialog
                    open={threadViewOpen === offer.id}
                    onOpenChange={(open) =>
                      setThreadViewOpen(open ? offer.id : null)
                    }
                  >
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="w-full">
                        <MessageCircle className="w-4 h-4 mr-1" />
                        메시지 보기
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh]">
                      <ThreadView
                        threadId={offer.dm_thread_id}
                        offerId={offer.id}
                        onClose={() => setThreadViewOpen(null)}
                      />
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            )}

            {offer.status === "completed" && (
              <div className="pt-2">
                <Badge className="bg-green-600">거래 완료</Badge>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
